import React, { useEffect, useState } from 'react';
import './webrosterAppLanding.css';

const APP_FEATURES = [
  { icon: 'fa-map-marker', title: 'GPS Clock-In', desc: 'Employees clock in and out with geolocation verification. Know exactly who clocked in, where, and when.' },
  { icon: 'fa-calendar-o', title: 'My Schedule', desc: 'View upcoming shifts, days off, and schedule changes in real time. Always know when to show up.' },
  { icon: 'fa-exchange', title: 'Shift Swap', desc: 'Request shift swaps or cover open shifts with a tap. Manager approval handled directly in the app.' },
  { icon: 'fa-bell', title: 'Push Notifications', desc: 'Get notified of schedule changes, swap approvals, and upcoming shifts before they sneak up on you.' },
  { icon: 'fa-wifi', title: 'Works Offline', desc: 'Core features work without internet. Syncs automatically when connectivity is restored.' },
  { icon: 'fa-lock', title: 'Biometric Login', desc: 'Sign in with Face ID or fingerprint. Fast, secure, and no passwords to forget.' },
];

const PhoneMock = ({ screen }) => (
  <div className="wa-phone">
    <div className="wa-phone-notch" />
    <div className="wa-phone-screen">
      {screen === 'clockin' && (
        <div className="wa-screen wa-screen-clockin">
          <div className="wa-screen-time">08:52</div>
          <div className="wa-screen-greeting">Good morning, Carlos</div>
          <div className="wa-clock-ring">
            <div className="wa-clock-inner">
              <span className="wa-clock-action">Tap to<br />Clock In</span>
            </div>
          </div>
          <div className="wa-screen-location">
            <i className="fa fa-map-marker" /> Cancun Branch
          </div>
          <div className="wa-screen-shift">Morning Shift &mdash; 9:00 AM</div>
        </div>
      )}
      {screen === 'schedule' && (
        <div className="wa-screen wa-screen-schedule">
          <div className="wa-sched-header">My Schedule</div>
          <div className="wa-sched-week">
            {['M','T','W','T','F'].map((d, i) => (
              <div key={i} className={`wa-sched-day${i === 2 ? ' is-today' : ''}`}>
                <span>{d}</span>
                <div className={`wa-sched-dot${i === 1 || i === 4 ? ' has-shift' : ''}`} />
              </div>
            ))}
          </div>
          <div className="wa-sched-list">
            {[
              { day: 'Today', label: 'Morning', time: '9:00 - 17:00', color: '#4f8ef7' },
              { day: 'Thu', label: 'Afternoon', time: '13:00 - 21:00', color: '#22d3ee' },
              { day: 'Fri', label: 'Off', time: '', color: '#3d5a80' },
            ].map((s, i) => (
              <div key={i} className="wa-sched-item">
                <div className="wa-sched-dot2" style={{ background: s.color }} />
                <div className="wa-sched-info">
                  <span>{s.day} &mdash; {s.label}</span>
                  {s.time && <small>{s.time}</small>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    <div className="wa-phone-home" />
  </div>
);

export const WebrosterAppLanding = () => {
  const [activeScreen, setActiveScreen] = useState('clockin');

  useEffect(() => {
    const prev = document.title;
    document.title = 'Webroster App | Your Workforce in Your Pocket';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="wa-page">

      {/* ── Nav ── */}
      <nav className="wa-nav">
        <div className="wa-nav-inner">
          <a href="/webroster-app" className="wa-nav-brand">
            <img src="/img/logo-webroster-app.png" alt="Webroster App" />
          </a>
          <div className="wa-nav-links">
            <a href="#wa-features">Features</a>
            <a href="#wa-cta" className="wa-nav-cta">Get Access</a>
          </div>
          <a href="/" className="wa-back">&#8592; Mindware</a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="wa-hero">
        <div className="wa-hero-bg" />
        <div className="wa-wrap wa-hero-inner">
          <div className="wa-hero-copy">
            <span className="wa-eyebrow">Employee Mobile App</span>
            <h1>
              Your workforce,<br />
              <span className="wa-grad">in your pocket.</span>
            </h1>
            <p>
              Webroster App puts schedule management, clock-in, and shift
              coordination in every employee's hands. Works on iOS and Android.
            </p>
            <div className="wa-hero-actions">
              <a href="#wa-cta" className="wa-btn-primary">Get Access</a>
              <a href="/webroster" className="wa-btn-ghost">See full platform &#8594;</a>
            </div>
            <div className="wa-store-badges">
              <div className="wa-store-badge">
                <i className="fa fa-apple" />
                <div><small>Download on the</small><strong>App Store</strong></div>
              </div>
              <div className="wa-store-badge">
                <i className="fa fa-android" />
                <div><small>Get it on</small><strong>Google Play</strong></div>
              </div>
            </div>
          </div>
          <div className="wa-hero-phones">
            <div className="wa-phone-tabs">
              {[['clockin','Clock In'],['schedule','Schedule']].map(([s, l]) => (
                <button
                  key={s}
                  className={`wa-phone-tab${activeScreen === s ? ' is-active' : ''}`}
                  onClick={() => setActiveScreen(s)}
                >
                  {l}
                </button>
              ))}
            </div>
            <PhoneMock screen={activeScreen} />
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="wa-features" className="wa-section wa-features-section">
        <div className="wa-wrap">
          <div className="wa-section-head">
            <span className="wa-label">Features</span>
            <h2>Built for the people doing the work</h2>
            <p>
              Webroster App is designed around the employee experience.
              Simple, fast, and reliable.
            </p>
          </div>
          <div className="wa-grid">
            {APP_FEATURES.map((f, i) => (
              <div key={i} className="wa-feat-card">
                <i className={`fa ${f.icon} wa-feat-icon`} />
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="wa-cta" className="wa-section wa-cta-section">
        <div className="wa-wrap wa-cta-inner">
          <span className="wa-label">Get Started</span>
          <h2>Give your team the app they deserve</h2>
          <p>
            Webroster App works alongside the Webroster platform.
            Contact us to set up access for your organization.
          </p>
          <a href="mailto:contact@mindware.com.mx" className="wa-btn-primary wa-btn-lg">
            Contact Us &nbsp;<i className="fa fa-arrow-right" />
          </a>
          <div className="wa-cta-sub">
            <a href="/webroster">See the full Webroster platform &#8594;</a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="wa-footer">
        <div className="wa-wrap wa-footer-inner">
          <span>Webroster App by <a href="/">Mindware</a></span>
          <a href="/">&#8592; Back to Mindware</a>
        </div>
      </footer>

    </div>
  );
};
